import type { MasterCodeItem } from '@/api/master-code.api';
import type { UserSearchItem } from '@/api/user.api';
import type React from 'react';
import type { GroupedUser, SelectOption, User } from '../types';

/**
 * Options containing code-to-label mappings for display purposes
 */
export interface CodeLabelOptions {
  statusOptions: SelectOption[];
  departmentOptions: SelectOption[];
  roleOptions: SelectOption[];
}

/**
 * Create a label-to-code mapper function from SelectOption array
 * @param options Array of SelectOption with value (code) and label
 * @returns Function that maps a label to its code, or returns the label if not found
 */
export const createLabelToCodeMapper = (
  options: SelectOption[],
): ((label: string | undefined) => string) => {
  const labelToCodeMap = new Map(
    options.map(opt => [opt.label.toLowerCase(), opt.value]),
  );
  return (label: string | undefined): string => {
    if (!label) {
      return '';
    }
    return labelToCodeMap.get(label.toLowerCase()) ?? label;
  };
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Apply group selection rules to handle parent-child checkbox relationships
 * When a group is selected, all children are selected. When children are selected, parent is auto-checked.
 * @param data Array of grouped user data
 * @param selectedRowKeys Previously selected row keys
 * @param newSelectedKeys Newly selected row keys (from user interaction)
 * @returns Updated selection with group rules applied
 */
export const applyGroupSelectionRules = (
  data: GroupedUser[],
  selectedRowKeys: React.Key[],
  newSelectedKeys: React.Key[],
): React.Key[] => {
  const updatedKeys = new Set(newSelectedKeys);

  for (const row of data) {
    if (!row.isGroup) {
      continue;
    }

    const children = getGroupChildren(data, row.key);
    const wasGroupSelected = selectedRowKeys.includes(row.key);
    const isGroupSelected = newSelectedKeys.includes(row.key);

    if (isGroupSelected && !wasGroupSelected) {
      children.forEach(childKey => updatedKeys.add(childKey));
    } else if (!isGroupSelected && wasGroupSelected) {
      children.forEach(childKey => updatedKeys.delete(childKey));
    } else {
      continue;
    }
  }

  for (const row of data) {
    if (!row.isGroup) {
      continue;
    }

    const children = getGroupChildren(data, row.key);
    const selectedChildren = children.filter(childKey =>
      updatedKeys.has(childKey),
    );

    if (selectedChildren.length > 0) {
      updatedKeys.add(row.key);
    } else {
      updatedKeys.delete(row.key);
    }
  }

  return Array.from(updatedKeys);
};

/**
 * Get children keys of a group from grouped data
 * This is a pure function that can be used for group selection logic
 */
export const getGroupChildren = (
  data: GroupedUser[],
  groupKey: string,
): React.Key[] => {
  const children: React.Key[] = [];
  let isInGroup = false;

  for (const row of data) {
    if (row.isGroup) {
      isInGroup = row.key === groupKey;
    } else if (isInGroup) {
      children.push(row.key);
    } else {
      continue;
    }
  }

  return children;
};

/**
 * Convert master code items to select options
 * @param codes Array of MasterCodeItem
 * @returns Array of SelectOption with code as value and description as label
 */
export const toSelectOptions = (codes: MasterCodeItem[]): SelectOption[] => {
  return codes.map(item => ({
    value: item.code,
    label: item.description,
  }));
};

/**
 * Get initials from name
 */
export const getInitials = (name: string): string => {
  if (!name) {
    return '';
  }
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * Get Ant Design tag color based on status label
 * @param statusLabel The status label (e.g., 'Active', 'Suspend')
 * @returns Ant Design tag color string
 */
export const getStatusColor = (statusLabel: string | undefined): string => {
  const statusLower = statusLabel?.toLowerCase();
  if (statusLower === 'active') {
    return 'success';
  }
  if (statusLower === 'suspend') {
    return 'error';
  }
  return 'default';
};

/**
 * Group users by their grouping field
 */
export const groupUsers = (users: User[]): GroupedUser[] => {
  const groupedData: GroupedUser[] = [];
  const groups = new Map<string, User[]>();

  users.forEach(user => {
    const group = user.grouping || 'Others';
    const groupedUsers = groups.get(group);
    if (groupedUsers) {
      groupedUsers.push(user);
    } else {
      groups.set(group, [user]);
    }
  });

  groups.forEach((users, groupName) => {
    // Add group header row
    groupedData.push({
      key: `group-${groupName}`,
      isGroup: true,
      groupingValue: groupName,
      userCount: users.length,
    });

    // Add user rows
    users.forEach(user => {
      groupedData.push({
        key: user.key,
        isGroup: false,
        userId: user.userId,
        userName: user.userName,
        userPicture: user.userPicture,
        email: user.email,
        department: user.department,
        role: user.role,
        status: user.status,
        lastLogin: user.lastLogin,
      });
    });
  });

  return groupedData;
};

/**
 * Group user search results by specified field
 * @param users Array of user search items from API
 * @param grouping The field to group by (Department, Status, Role)
 * @param options Optional code-to-label mapping options for displaying group labels
 */
export const groupUserSearchResults = (
  users: UserSearchItem[],
  grouping = '',
): GroupedUser[] => {
  if (!grouping) {
    // No grouping, return flat list of users
    return users.map(user => ({
      key: user.userId,
      isGroup: false,
      userId: user.userId,
      userName: user.userName,
      userPicture: user.photo ?? undefined,
      email: user.email,
      department: user.department,
      role: user.role,
      status: user.status,
      lastLogin: user.lastLoginTime ?? undefined,
    }));
  }

  const groupedData: GroupedUser[] = [];
  const groups = new Map<string, UserSearchItem[]>();
  // Explicitly type fieldKey as a key of UserSearchItem to ensure type safety
  const fieldKey = grouping as keyof UserSearchItem;

  // Group users by the specified field (use raw value for group key)
  users.forEach(user => {
    const groupValue = String(user[fieldKey] ?? '');
    const groupedUsers = groups.get(groupValue);
    if (groupedUsers) {
      groupedUsers.push(user);
    } else {
      groups.set(groupValue, [user]);
    }
  });

  // Sort groups alphabetically
  const sortedGroups = Array.from(groups.entries()).sort((a, b) =>
    a[0].localeCompare(b[0]),
  );

  sortedGroups.forEach(([groupName, groupUsers]) => {
    // Add group header row
    groupedData.push({
      key: `group-${groupName}`,
      isGroup: true,
      groupingValue: groupName,
      userCount: groupUsers.length,
    });

    // Add user rows - keep codes as-is from API
    groupUsers.forEach(user => {
      groupedData.push({
        key: user.userId,
        isGroup: false,
        userId: user.userId,
        userName: user.userName,
        userPicture: user.photo ?? undefined,
        email: user.email,
        department: user.department,
        role: user.role,
        status: user.status,
        lastLogin: user.lastLoginTime ?? undefined,
      });
    });
  });

  return groupedData;
};
